import React, { useEffect, useState, useRef, useMemo, memo, useCallback } from 'react';
import { getBlueprints, getResponsiveBlueprints, calculateAbsolutePosition } from './core';

// ========================
// SHIMMER ANIMATION
// ========================

const shimmerKeyframes = `
@keyframes ghostlayout-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@keyframes ghostlayout-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.textContent = shimmerKeyframes;
  document.head.appendChild(style);
  stylesInjected = true;
}

// ========================
// BLUEPRINT RENDERER (memoized)
// ========================

const BlueprintElement = memo(function BlueprintElement({ blueprint, containerWidth, animation, theme, debug }) {
  const pos = calculateAbsolutePosition(blueprint, containerWidth);
  const isDark = theme === 'dark';

  const baseColor = isDark ? '#2A2A2A' : '#E0E0E0';
  const highlightColor = isDark ? '#3A3A3A' : '#F5F5F5';

  const blueprintStyle = {
    position: 'absolute',
    left: pos.left,
    top: pos.top,
    width: pos.width,
    height: pos.height,
    borderRadius: pos.borderRadius,
    overflow: 'hidden',
  };

  if (animation === 'shimmer') {
    blueprintStyle.background = `linear-gradient(90deg, ${baseColor} 0%, ${highlightColor} 50%, ${baseColor} 100%)`;
    blueprintStyle.backgroundSize = '200% 100%';
    blueprintStyle.animation = 'ghostlayout-shimmer 2s infinite linear';
  } else if (animation === 'pulse') {
    blueprintStyle.backgroundColor = baseColor;
    blueprintStyle.animation = 'ghostlayout-pulse 1.5s ease-in-out infinite';
  } else {
    blueprintStyle.backgroundColor = baseColor;
  }

  if (debug) {
    blueprintStyle.outline = '1px solid #FF3B30';
    blueprintStyle.outlineOffset = '-1px';
  }

  return <div style={blueprintStyle} />;
});

// ========================
// SKELETON RENDERER
// ========================

export const SkeletonRenderer = memo(function SkeletonRenderer({
  blueprintData,
  animation = 'shimmer',
  theme = 'light',
  debug = false,
  minHeight,
}) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [blueprints, setBlueprints] = useState([]);

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        const responsiveBlueprints = getResponsiveBlueprints(blueprintData.breakpoints, window.innerWidth);
        setBlueprints(responsiveBlueprints);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [blueprintData]);

  const maxHeight = useMemo(() => {
    if (blueprints.length > 0) return Math.max(...blueprints.map(b => b.y + b.h));
    return minHeight || 100;
  }, [blueprints, minHeight]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: maxHeight,
        minHeight: minHeight || 'auto',
        overflow: 'hidden',
      }}
      data-testid="skeleton-renderer"
    >
      {blueprints.map((blueprint, index) => (
        <BlueprintElement
          key={index}
          blueprint={blueprint}
          containerWidth={containerWidth}
          animation={animation}
          theme={theme}
          debug={debug}
        />
      ))}
      {debug && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            padding: '2px 6px',
            background: '#FF3B30',
            color: 'white',
            fontSize: '9px',
            fontFamily: 'JetBrains Mono, monospace',
            zIndex: 9999,
            letterSpacing: '0.05em',
          }}
          data-testid="debug-overlay"
        >
          {blueprints.length} BLUEPRINTS | {typeof window !== 'undefined' ? window.innerWidth : 0}PX
        </div>
      )}
    </div>
  );
});

// ========================
// SKELETON WRAPPER COMPONENT
// ========================

export function Skeleton({
  name,
  loading,
  animation = 'shimmer',
  theme = 'light',
  debug = false,
  minHeight,
  fixture,
  children,
}) {
  // During CLI extraction: render fixture (if provided) or children
  // so the CLI can measure the actual layout
  const isExtractMode = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('ghostlayout-extract') === 'true';

  if (isExtractMode) {
    return <>{fixture || children}</>;
  }

  if (!loading) {
    return <>{children}</>;
  }

  const blueprintData = getBlueprints(name);

  if (!blueprintData) {
    return (
      <div
        style={{
          minHeight: minHeight || 100,
          background: theme === 'dark' ? '#1A1A1A' : '#F5F5F5',
          borderRadius: 0,
          border: theme === 'dark' ? '1px solid #2A2A2A' : '1px solid #E5E5E5',
        }}
        data-testid={`skeleton-fallback-${name}`}
      />
    );
  }

  return (
    <SkeletonRenderer
      blueprintData={blueprintData}
      animation={animation}
      theme={theme}
      debug={debug}
      minHeight={minHeight}
    />
  );
}
